import type { AppErrorIssue, AppErrorsLookup } from "@central-hub/contracts";
import { useState } from "react";
import type { Session } from "./auth";
import { ago, clock, clockSeconds, dayClock, plural } from "./format";
import { useLookup } from "./hooks";
import { Icon } from "./icons";
import { detailHref } from "./links";
import { type AppErrorSite, type ErrorBoard, type ErrorSite, webLink } from "./model";

const SEVERITY_LABEL = { fatal: "Fatal", error: "Error", warning: "Warning" } as const;

/**
 * The errors view: what every target's logs showed in the last window (its pods' error lines
 * and the answers it gave through the ingress), its most repeated messages, and which of the
 * last 24 hours had errors.
 */
export function ErrorsView({
  board,
  apps = [],
  session,
  now,
  stale,
}: {
  board: ErrorBoard | null;
  /** Errors the applications report themselves (apperrors module). */
  apps?: readonly AppErrorSite[];
  session?: Session;
  now: number;
  stale: boolean;
}) {
  const appSection = apps.length > 0 && (
    <AppErrorsSection sites={apps} session={session} now={now} />
  );
  if (!board) {
    return (
      <main className="view" data-stale={stale}>
        {appSection}
        {!appSection && (
          <p className="muted">
            The hub doesn’t read logs: enable <code>cluster.logs</code> in the kubernetes module.
          </p>
        )}
      </main>
    );
  }
  const ratio = board.requests > 0 ? (board.http5xx / board.requests) * 100 : 0;
  return (
    <main className="view" data-stale={stale}>
      {appSection}
      <ul className="tally" aria-label="Errors at a glance">
        <li className="tally-item" data-status={board.withFatal.length > 0 ? "down" : "up"}>
          <Icon name={board.withFatal.length > 0 ? "down" : "up"} />
          <b className="num">{board.withFatal.length}</b>
          <span>with fatal errors</span>
          <Names names={board.withFatal} />
        </li>
        <li className="tally-item" data-status={board.with5xx.length > 0 ? "degraded" : "up"}>
          <Icon name={board.with5xx.length > 0 ? "degraded" : "up"} />
          <b className="num">{board.with5xx.length}</b>
          <span>answering 5xx</span>
          <Names names={board.with5xx} />
        </li>
        <li className="tally-item" data-status={board.withWarnings > 0 ? "degraded" : "up"}>
          <Icon name={board.withWarnings > 0 ? "degraded" : "up"} />
          <b className="num">{board.withWarnings}</b>
          <span>logging errors or warnings</span>
        </li>
        <li className="tally-item" data-status="up">
          <Icon name="up" />
          <b className="num">{board.requests.toLocaleString()}</b>
          <span>
            requests in the last {board.windowMinutes} min
            {board.http5xx > 0 && `, ${board.http5xx} 5xx (${ratio.toFixed(1)}%)`}
          </span>
        </li>
        {board.unread.length > 0 && (
          <li className="tally-item" data-status="unknown">
            <Icon name="unknown" />
            <b className="num">{board.unread.length}</b>
            <span>whose logs couldn’t be read</span>
            <Names names={board.unread} />
          </li>
        )}
      </ul>

      <section className="panel" aria-labelledby="errors-title">
        <div className="panel-head">
          <h2 id="errors-title" className="eyebrow">
            Sites
          </h2>
          <span className="num">
            {board.readAt
              ? `${clock(board.readAt - board.windowMinutes * 60_000)}–${clock(board.readAt)}`
              : `last ${board.windowMinutes} min`}{" "}
            · {plural(board.sites.length, "site", "sites")}
          </span>
        </div>
        <ul className="errors">
          {board.sites.map((site) => (
            <SiteErrors key={site.target} site={site} />
          ))}
        </ul>
      </section>
    </main>
  );
}

function Names({ names }: { names: readonly string[] }) {
  if (names.length === 0) return null;
  return (
    <span className="tally-names">
      {names.map((name, i) => (
        <span key={name}>
          {i > 0 && ", "}
          <a href={detailHref(name)}>{name}</a>
        </span>
      ))}
    </span>
  );
}

function SiteErrors({ site }: { site: ErrorSite }) {
  const unread = site.status === "unknown";
  const counts = unread
    ? []
    : [
        site.requests !== null && {
          one: "request",
          many: "requests",
          n: site.requests,
          bad: false,
        },
        site.http5xx !== null && {
          one: "5xx",
          many: "5xx",
          n: site.http5xx,
          bad: site.http5xx > 0,
        },
        { one: "fatal", many: "fatal", n: site.fatal, bad: site.fatal > 0 },
        { one: "error", many: "errors", n: site.error, bad: site.error > 0 },
        { one: "warning", many: "warnings", n: site.warning, bad: site.warning > 0 },
      ].filter((c): c is { one: string; many: string; n: number; bad: boolean } => Boolean(c));
  return (
    <li className="errors-site" data-status={site.status}>
      <div className="errors-head">
        <Icon name={site.status} />
        <a className="errors-name" href={detailHref(site.target)}>
          {site.target}
        </a>
        <ul className="errors-counts" aria-label={`${site.target}: last window`}>
          {counts.map((c) => (
            <li key={c.many} data-bad={c.bad}>
              <b className="num">{c.n.toLocaleString()}</b> {c.n === 1 ? c.one : c.many}
            </li>
          ))}
        </ul>
        <span
          className="errors-hours"
          role="img"
          aria-label={`Hours with errors in the last 24 h: ${site.hours.filter((h) => h === "degraded" || h === "down").length}`}
          title="Last 24 hours: an hour is marked when its logs showed fatal errors or many 5xx"
        >
          {site.hours.map((h, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed hourly slots
            <span key={i} data-status={h} />
          ))}
        </span>
      </div>
      {unread && <p className="muted errors-note">{site.message}</p>}
      {site.top.length > 0 && (
        <ol className="errors-top">
          {site.top.map((m) => (
            <li key={m.text} data-status={m.severity === "warning" ? "degraded" : "down"}>
              <span className="errors-sev">{SEVERITY_LABEL[m.severity]}</span>
              <span className="num errors-n">×{m.count}</span>
              <span className="num errors-when" title="Last logged">
                {m.last ? clockSeconds(m.last) : ""}
              </span>
              <code className="errors-text">{m.text}</code>
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}

const ISSUES_MS = 60_000;

/** What the applications report themselves: one row per site, its issues on demand. */
function AppErrorsSection({
  sites,
  session,
  now,
}: {
  sites: readonly AppErrorSite[];
  session?: Session;
  now: number;
}) {
  return (
    <section className="panel" aria-labelledby="app-errors-title">
      <div className="panel-head">
        <h2 id="app-errors-title" className="eyebrow">
          Application errors
        </h2>
        <span className="num">
          last {sites[0]?.windowDays ?? 7} days · {plural(sites.length, "site", "sites")}
        </span>
      </div>
      <ul className="errors">
        {sites.map((site) => (
          <AppErrorsSite key={site.target} site={site} session={session} now={now} />
        ))}
      </ul>
    </section>
  );
}

function AppErrorsSite({
  site,
  session,
  now,
}: {
  site: AppErrorSite;
  session?: Session;
  now: number;
}) {
  const [open, setOpen] = useState(site.status === "degraded");
  const counts = [
    { one: "error", many: "errors", n: site.errors24h, bad: site.errors24h > 0 },
    { one: "warning", many: "warnings", n: site.warnings24h, bad: false },
    { one: "new issue", many: "new issues", n: site.newIssues24h, bad: site.newIssues24h > 0 },
  ];
  return (
    <li className="errors-site" data-status={site.status}>
      <div className="errors-head">
        <Icon name={site.status} />
        <a className="errors-name" href={detailHref(site.target)}>
          {site.target}
        </a>
        <ul className="errors-counts" aria-label={`${site.target}: last 24 h`}>
          {counts.map((c) => (
            <li key={c.many} data-bad={c.bad}>
              <b className="num">{c.n.toLocaleString()}</b> {c.n === 1 ? c.one : c.many}
            </li>
          ))}
          <li>
            <span className="muted">
              {site.lastAt ? `last ${ago(now - site.lastAt)}` : "none reported"}
            </span>
          </li>
        </ul>
        {site.issues > 0 && session && (
          <button type="button" className="linkbtn" onClick={() => setOpen(!open)}>
            {open ? "Hide issues" : plural(site.issues, "issue", "issues")}
          </button>
        )}
      </div>
      <p className="muted errors-note">{site.message}</p>
      {open && session && <AppIssues session={session} target={site.target} now={now} />}
    </li>
  );
}

function AppIssues({ session, target, now }: { session: Session; target: string; now: number }) {
  const { data, error } = useLookup<AppErrorsLookup>(
    session,
    "apperrors",
    "issues",
    { target },
    ISSUES_MS,
  );
  if (error && !data) return <p className="muted errors-note">Couldn’t load issues: {error}</p>;
  if (!data) return <p className="muted errors-note">Loading…</p>;
  return (
    <ol className="app-issues">
      {data.issues.map((issue) => (
        <AppIssueRow key={issue.id} issue={issue} now={now} />
      ))}
      {data.truncated && <li className="muted">Older issues not shown.</li>}
    </ol>
  );
}

/** An issue: its line, and when opened, its latest event in full. */
function AppIssueRow({ issue, now }: { issue: AppErrorIssue; now: number }) {
  const latest = issue.latest;
  const url = webLink(latest.url);
  const hit = issue.level === "error" ? "down" : "degraded";
  return (
    <li data-status={hit}>
      <details>
        <summary>
          <span className="errors-sev">{issue.level === "error" ? "Error" : "Warning"}</span>
          <span className="num errors-n">×{issue.count}</span>
          <span className="num errors-when" title={`Last seen ${dayClock(issue.lastSeen)}`}>
            {ago(now - Date.parse(issue.lastSeen))}
          </span>
          <code className="errors-text">{issue.title}</code>
          <span className="muted app-issue-meta">
            {issue.source === "browser" ? "browser" : "server"}
            {issue.culprit && ` · ${issue.culprit}`}
            {issue.users > 0 && ` · ${plural(issue.users, "user", "users")}`}
          </span>
          <span
            className="errors-hours"
            role="img"
            aria-label={`Events in the last 24 h: ${issue.hours.reduce((a, b) => a + b, 0)}`}
          >
            {issue.hours.map((n, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed hourly slots
              <span key={i} data-status={n > 0 ? hit : "up"} />
            ))}
          </span>
        </summary>
        <dl className="app-issue-facts">
          <dt>First seen</dt>
          <dd className="num">{dayClock(issue.firstSeen)}</dd>
          <dt>Last seen</dt>
          <dd className="num">{dayClock(issue.lastSeen)}</dd>
          {issue.userNames.length > 0 && (
            <>
              <dt>Users</dt>
              <dd>
                {issue.userNames.join(", ")}
                {issue.users > issue.userNames.length &&
                  ` +${issue.users - issue.userNames.length} more`}
              </dd>
            </>
          )}
          {latest.url && (
            <>
              <dt>Where</dt>
              <dd>
                {url ? (
                  <a href={url} target="_blank" rel="noreferrer">
                    {latest.url}
                  </a>
                ) : (
                  latest.url
                )}
              </dd>
            </>
          )}
          {latest.release && (
            <>
              <dt>Release</dt>
              <dd className="num">{latest.release}</dd>
            </>
          )}
          {Object.entries(latest.context ?? {}).map(([key, value]) => (
            <Fact key={key} name={key} value={String(value)} />
          ))}
        </dl>
        <pre className="evidence-lines">{latest.stack ?? latest.message}</pre>
      </details>
    </li>
  );
}

function Fact({ name, value }: { name: string; value: string }) {
  return (
    <>
      <dt>{name}</dt>
      <dd>{value}</dd>
    </>
  );
}
